/**
 * Which inbound CareCommunications have been opened with Vis or from the
 * patient Medcom list. This browser keeps a copy. The clinic copy is loaded
 * separately so another computer sees the same letters as read.
 */

export interface ReadStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface InboundLetter {
  id: string;
  direction: string;
  communicationIdentifier?: string | null;
}

const memoryReadIds = new Map<string, Set<string>>();

/** Test isolation. Production calls never need this. */
export function resetReadMemory(): void {
  memoryReadIds.clear();
}

function remembered(clinicId: string): Set<string> {
  let ids = memoryReadIds.get(clinicId);
  if (!ids) {
    ids = new Set();
    memoryReadIds.set(clinicId, ids);
  }
  return ids;
}

const EVENT = 'clinio-medcom-read';

function readKey(clinicId: string): string {
  return `clinio.medcom.read.${clinicId}`;
}

export function loadReadIds(storage: ReadStorage, clinicId: string): Set<string> {
  const ids = new Set<string>();
  try {
    const raw = storage.getItem(readKey(clinicId));
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        for (const id of parsed) {
          if (typeof id === 'string' && id.length > 0) ids.add(id);
        }
      }
    }
  } catch {
    // Blocked or damaged storage. The in-memory ids still apply.
  }
  for (const id of remembered(clinicId)) ids.add(id);
  return ids;
}

function saveReadIds(storage: ReadStorage, clinicId: string, ids: Set<string>): void {
  storage.setItem(readKey(clinicId), JSON.stringify([...ids]));
}

/** Adds every new id. Returns true when the saved set grew. */
export function rememberReadIds(
  storage: ReadStorage,
  clinicId: string,
  messageIds: Iterable<string>,
): boolean {
  const ids = loadReadIds(storage, clinicId);
  const memory = remembered(clinicId);
  let added = false;
  for (const id of messageIds) {
    if (!id || ids.has(id)) continue;
    ids.add(id);
    memory.add(id);
    added = true;
  }
  if (!added) return false;
  try {
    saveReadIds(storage, clinicId, ids);
  } catch {
    // The in-memory set still hides Ulæst for this session.
  }
  return true;
}

/** Returns true when this id was not already recorded. */
export function markMessageRead(
  storage: ReadStorage,
  clinicId: string,
  messageId: string,
): boolean {
  if (!messageId) return false;
  return rememberReadIds(storage, clinicId, [messageId]);
}

export function isInboundUnread(letter: InboundLetter, readIds: Set<string>): boolean {
  if (letter.direction !== 'in') return false;
  if (readIds.has(letter.id)) return false;
  if (letter.communicationIdentifier && readIds.has(letter.communicationIdentifier)) return false;
  return true;
}

export function unreadCountLabel(count: number): string {
  return count === 1 ? '1 ulæst' : `${count} ulæste`;
}

export function notifyCareCommunicationRead(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function careCommunicationReadEvent(): string {
  return EVENT;
}
